import React, { useEffect, useState } from "react";
import {
    Box, Button, Typography, TextField, Dialog, DialogTitle,
    DialogContent, DialogActions, CircularProgress
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
    fetchTpaLogos, addTpaLogo, editTpaLogo,
    updateTpaLogo, trashTpaLogo
} from "../../Adapters/ApiAdapter";
import { baseImage_path } from "../../variables/Variables";
import { toast, ToastContainer } from "react-toastify";

const HomeTPAEmpanelledLogos = () => {
    const [logos, setLogos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({ name: "", logo: null });
    const [editItem, setEditItem] = useState(null);
    const [loadingAction, setLoadingAction] = useState(false);

    const loadData = async () => {
        try {
            const res = await fetchTpaLogos();
            setLogos(res.data || []);
        } catch {
            toast.error("Failed to fetch logos");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, []);

    const handleFileChange = (e) => {
        setFormData({ ...formData, logo: e.target.files[0] });
    };

    const handleAdd = async () => {
        if (!formData.logo) {
            return toast.error("Logo is required.");
        }

        const data = new FormData();
        data.append("name", formData.name);
        data.append("logo", formData.logo);

        setLoadingAction(true);
        try {
            await addTpaLogo(data);
            toast.success("Logo added successfully");
            setFormData({ name: "", logo: null });
            loadData();
        } catch {
            toast.error("Failed to add logo");
        } finally {
            setLoadingAction(false);
        }
    };

    const handleEdit = async (id) => {
        try {
            const res = await editTpaLogo(id);
            setEditItem(res.data);
        } catch {
            toast.error("Failed to fetch logo details");
        }
    };

    const handleUpdate = async () => {
        const data = new FormData();
        data.append("name", editItem.name || "");
        if (editItem.logo instanceof File) {
            data.append("logo", editItem.logo);
        }

        setLoadingAction(true);
        try {
            await updateTpaLogo(editItem.id, data);
            toast.success("Updated successfully");
            setEditItem(null);
            loadData();
        } catch {
            toast.error("Update failed");
        } finally {
            setLoadingAction(false);
        }
    };

    const handleTrash = async (id) => {
        try {
            await trashTpaLogo(id);
            toast.success("Logo deleted");
            loadData();
        } catch {
            toast.error("Delete failed");
        }
    };

    const columns = [
        { field: "id", headerName: "ID", width: 90 },
        {
            field: "logo",
            headerName: "Logo",
            width: 120,
            renderCell: (params) =>
                <img
                    src={`${baseImage_path}/${params.row.logo}`}
                    alt="TPA Logo"
                    style={{ width: 50, height: 50, objectFit: "contain" }}
                />
        },
        { field: "name", headerName: "Name", width: 200 },
        {
            field: "actions",
            headerName: "Actions",
            width: 200,
            renderCell: (params) => (
                <Box>
                    <Button variant="contained" size="small" onClick={() => handleEdit(params.row.id)}>
                        Edit
                    </Button>
                    <Button
                        variant="contained"
                        size="small"
                        color="error"
                        sx={{ ml: 1 }}
                        onClick={() => handleTrash(params.row.id)}
                    >
                        Trash
                    </Button>
                </Box>
            )
        }
    ];

    return (
        <Box m={2}>
            <ToastContainer />
            <Typography variant="h4" gutterBottom>TPA Empanelled Logos</Typography>

            <Box display="flex" alignItems="center" gap={2} mb={2}>
                <TextField
                    label="Name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
                <input type="file" onChange={handleFileChange} />
                <Button
                    variant="contained"
                    onClick={handleAdd}
                    disabled={loadingAction}
                >
                    {loadingAction ? <CircularProgress size={24} /> : "Add"}
                </Button>
            </Box>

            <DataGrid
                rows={logos}
                columns={columns}
                getRowId={(row) => row.id}
                autoHeight
                loading={loading}
            />

            {/* Edit Dialog */}
            {editItem && (
                <Dialog open onClose={() => setEditItem(null)}>
                    <DialogTitle>Edit Logo</DialogTitle>
                    <DialogContent>
                        <TextField
                            fullWidth
                            margin="normal"
                            label="Name"
                            value={editItem.name || ""}
                            onChange={(e) =>
                                setEditItem({ ...editItem, name: e.target.value })
                            }
                        />
                        <input
                            type="file"
                            onChange={(e) =>
                                setEditItem({ ...editItem, logo: e.target.files[0] })
                            }
                            style={{ marginTop: 16 }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setEditItem(null)}>Cancel</Button>
                        <Button variant="contained" onClick={handleUpdate} disabled={loadingAction}>
                            {loadingAction ? <CircularProgress size={24} /> : "Update"}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </Box>
    );
};

export default HomeTPAEmpanelledLogos;
